import { Button, Sheet, SheetContent, SheetTitle, SheetTrigger } from '@invent/wl-ui-kit-next/ui';
import { Menu } from 'lucide-react';
import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import {
  ZoneProvider,
  useZoneRoot,
  type IAppBar,
  type IAppRefresh,
  type IRecordVisit,
  type WorkspaceWidth
} from '@/design';
import { ExportDownloads } from '@/domains/admin';
import { EmailVerificationBanner, useAuth, useCan } from '@/domains/auth';
import { CommandPalette, findLabel } from './command-palette';
import { useRecentRecords } from './recent-records';
import { RecentMenu, TeamHeader } from './team-header';
import { TeamRail, useRailFolds } from './team-rail';

const RAIL_KEY = 'veritant.team.rail-collapsed';

/** What a Team Portal page may ask of its shell: the palette, and the recent records. */
interface ITeamShellContext {
  openPalette: () => void;
  recent: IRecordVisit[];
}

const TeamShellContext = React.createContext<ITeamShellContext>({
  openPalette: () => undefined,
  recent: []
});

export function useTeamShell(): ITeamShellContext {
  return React.useContext(TeamShellContext);
}

/**
 * The Team Portal's shell: the rail, the header, the address prompt, the working area (the
 * two-portals design, §4.2).
 *
 * It is the Team Portal's alone — no navigation component, link or route of the Advisor Portal is
 * reachable from it, and it imports nothing from `zones/portal`. The one thing both shells draw is
 * the address prompt, which is the auth domain's and says the same to everybody.
 *
 * **Designed for a desktop, and still usable on a narrow screen.** Below 1024 px the rail is not
 * drawn; the same rail opens as a sheet from "Menu" in the header, which wraps, so a phone gets the
 * whole zone and no page wider than itself. Only one of the two is ever in the accessibility tree,
 * so the "Team Portal" landmark stays unique.
 *
 * **The shell draws no `main`.** A screen is either a page, wrapped in one by the zone's routes
 * (`TeamMain`), or a list beside a record, where the record is the one `main` and the list a
 * complementary pane (`SplitView`) — so the landmark is always the thing a reader came to read.
 *
 * **The rail makes room when a screen asks for it** (`useWorkspaceWidth`): a list beside a record
 * collapses it to its icons below 1440 px, where the record would otherwise get well under the
 * 900 px it needs, and the version editor on any screen, because an author works across its whole
 * width. The rail's own switch still opens it over that for the visit, and a viewer's own choice
 * is untouched underneath.
 *
 * **A work page may draw its compact bar over the header's row** (`appBar`, `condense.tsx`), the
 * header's own controls hidden under it.
 *
 * **An export let go by its dialog stays in view on every page** (`ExportDownloads`, bottom-right)
 * until it is saved and dismissed; the shell going stops it.
 *
 * Ctrl/⌘+K opens the command palette from anywhere in the zone. The recent records it and the
 * header offer are kept per account and cleared when that account signs out here.
 *
 * **The account is re-read each time the tab is shown** (SR-228; the role-model UI design's §3).
 * survey-api reads an account's permissions afresh on every request (its D4), and an
 * administrator changes a role's or an account's in another browser, so a tab left open would
 * draw yesterday's sections until a 403 asked. On a shown tab `refreshUser` brings `permissions`
 * level, and every guard, rail entry and control re-renders from them — a section taken away
 * falls to Home through its `AdminRoute`. Nothing polls: a tab in view is one the viewer is
 * working in, and its 403s ask the same question. The address prompt re-reads on the same event
 * while it is drawn, for its own reason; the second read is the price of neither knowing of the
 * other.
 */
export function TeamShell() {
  useZoneRoot('team');
  const { user, signout, refreshUser } = useAuth();
  React.useEffect(() => {
    const reread = () => {
      if (document.visibilityState === 'visible') void refreshUser();
    };
    document.addEventListener('visibilitychange', reread);
    return () => document.removeEventListener('visibilitychange', reread);
  }, [refreshUser]);
  const recent = useRecentRecords(user?.accountId ?? null);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => readFlag(RAIL_KEY));
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const { pathname } = useLocation();
  const [asked, setAsked] = React.useState<Record<WorkspaceWidth, number>>({ split: 0, full: 0 });
  const [peek, setPeek] = React.useState(false);
  const belowWide = useMediaQuery('(max-width: 1439px)');
  const forced = asked.full > 0 || (asked.split > 0 && belowWide);
  React.useEffect(() => setPeek(false), [forced]);

  const onWorkspace = React.useCallback((width: WorkspaceWidth) => {
    setAsked((current) => ({ ...current, [width]: current[width] + 1 }));
    return () => setAsked((current) => ({ ...current, [width]: current[width] - 1 }));
  }, []);

  // A press in the sheet navigates; the sheet goes with the page it was opened over.
  React.useEffect(() => setSheetOpen(false), [pathname]);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleRail = () => {
    if (forced) {
      setPeek((current) => !current);
      return;
    }
    setCollapsed((current) => {
      writeFlag(RAIL_KEY, !current);
      return !current;
    });
  };

  const signOut = () => {
    recent.clear();
    signout();
  };

  const folds = useRailFolds();

  const shell = React.useMemo(
    () => ({ openPalette: () => setPaletteOpen(true), recent: recent.records }),
    [recent.records]
  );
  const can = useCan();
  const [header, setHeader] = React.useState<Nullable<HTMLElement>>(null);
  const [compact, setCompact] = React.useState(false);
  const [pageRefresh, setPageRefresh] = React.useState<Nullable<IAppRefresh>>(null);
  const appBar = React.useMemo<IAppBar>(
    () => ({
      host: header,
      findLabel: findLabel(can),
      onFind: shell.openPalette,
      recent: <RecentMenu recent={recent.records} compact />,
      refresh: pageRefresh,
      setRefresh: setPageRefresh,
      setCompact
    }),
    [header, can, shell.openPalette, recent.records, pageRefresh]
  );

  return (
    <ZoneProvider
      zone="team"
      viewer={user?.accountId}
      onRecord={recent.add}
      onWorkspace={onWorkspace}
      appBar={appBar}
    >
      <TeamShellContext.Provider value={shell}>
        <div className="flex min-h-screen bg-background text-[0.8125rem] text-foreground">
          <TeamRail
            collapsed={forced ? !peek : collapsed}
            onToggle={toggleRail}
            folds={folds}
            onSignOut={signOut}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <TeamHeader
              onOpenPalette={shell.openPalette}
              recent={recent.records}
              refresh={pageRefresh}
              host={setHeader}
              compact={compact}
              menu={
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button type="button" variant="ghost" size="sm" className="gap-1.5 lg:hidden">
                      <Menu className="size-4" aria-hidden />
                      Menu
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-62 border-0 p-0">
                    <SheetTitle className="sr-only">Team Portal</SheetTitle>
                    <TeamRail
                      inSheet
                      collapsed={false}
                      onToggle={toggleRail}
                      folds={folds}
                      onSignOut={signOut}
                    />
                  </SheetContent>
                </Sheet>
              }
            />
            <EmailVerificationBanner />
            <div className="flex min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            recent={recent.records}
          />
          <ExportDownloads />
        </div>
      </TeamShellContext.Provider>
    </ZoneProvider>
  );
}

/** Whether a media query matches, kept in step with the window. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** A Team Portal page's one `main` — every screen but a list beside a record, which draws its
 *  own (`SplitView`). The zone's routes wrap the pages in it. */
export function TeamMain() {
  return (
    <main className="min-w-0 flex-1">
      <Outlet />
    </main>
  );
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // A refused store keeps the rail as set for this page load.
  }
}
